import axios, { isAxiosError } from 'axios';
import axiosInstance from 'src/config/axios.config';

import type { BranchWorkingHour, ShiftScope } from 'src/api/branch_api_data';

export const API_ENDPOINTS = {
  AUTH: '/api/auth',
  USERS: '/api/users',
  EMPLOYEES: '/api/employees',
  COMPANIES: '/api/companies',
  LEAVE: '/api/leave',
  BRANCHES: '/api/branches',
  PRODUCTS: '/api/products',
};

export enum EmployeeStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  ON_LEAVE = 'ON_LEAVE',
  BANNED = 'BANNED',
  REQUIRES_VERIFICATION = 'REQUIRES_VERIFICATION'
}

export interface EmployeeDto {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  position: string;
  status: EmployeeStatus;
  branchId: string;
  branchName: string;
  deviceId: string | null;
  key: string | null;
  lastCheckInTime: string | null;
  totalWorkHours: number;
  attendanceCount: number;
  verified: boolean;
  deleted: boolean;
  // Ilk cihaz onayi bekleyen calisan (backend hesaplar: INACTIVE + attendanceCount==0 + deviceId).
  // Eski backend yanitlarinda bulunmayabilir; yoksa false kabul edilir (guvenli taraf).
  awaitingDeviceApproval?: boolean;
}

interface PageResponse<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
  size: number;
  number: number;
}

export const AllUsers = async (
  userId: string,
  page = 0,
  size = 5,
  sortBy = 'firstName',
  sortDirection = 'asc',
  search = ''
) => {
  try {
    const response = await axiosInstance.get(`${API_ENDPOINTS.EMPLOYEES}/my-employees`, {
      params: {
        page,
        size,
        sortBy,
        sortDirection,
        ...(search.trim() && { search: search.trim() })
      }
    });
    return response.data;
  } catch (error) {
    if (isAxiosError(error)) {
      if (error.response?.status === 403) {
        throw new Error('Bu işlem için ADMIN veya SUPER_ADMIN yetkisi gerekiyor');
      }
      throw new Error(error.response?.data?.message || 'Çalışanlar getirilirken bir hata oluştu');
    }
    throw error;
  }
};

export interface UpdateEmployeeDto {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  position: string;
  // Live clock-in status is NOT settable via the generic update anymore (backend ignores it). Account
  // lifecycle goes through approveEmployee/banEmployee/unbanEmployee. Kept optional for legacy callers.
  status?: EmployeeStatus;
  branchId: string;
  branchName: string;
  verified: boolean;
  deleted: boolean;
}

// Admin tarafından düzenlenebilir alanlar (verified ve deleted dahil değil)
export interface AdminUpdateEmployeeDto {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  position: string;
  // See UpdateEmployeeDto: status is no longer written by the generic update; use the lifecycle endpoints.
  status?: EmployeeStatus;
  branchId?: string;
  branchName?: string;
}

// Kullanıcı detaylarını getir
export async function getEmployeeById(id: string): Promise<EmployeeDto> {
  const response = await axiosInstance.get(`${API_ENDPOINTS.EMPLOYEES}/${id}`);
  return response.data;
}

// ===============================
// VARDİYA ATAMA (SHIFT ASSIGNMENT) API
// ===============================

// Vardiya özeti (atama içinde gömülü döner).
export interface ShiftSummary {
  id: string;
  name: string;
  scope: ShiftScope;
  isDefault: boolean;
  workingHours: BranchWorkingHour[];
}

// Bir çalışana ait vardiya ataması (tarih aralıklı).
export interface AssignmentDto {
  id: string;
  employeeId: string;
  shift: ShiftSummary;
  effectiveFrom: string; // "YYYY-MM-DD"
  effectiveTo: string | null; // "YYYY-MM-DD" | null (null => devam ediyor)
}

// Atama oluşturma gövdesi.
export interface AssignmentCreateDto {
  shiftId: string;
  effectiveFrom: string; // "YYYY-MM-DD"
  effectiveTo?: string | null; // "YYYY-MM-DD" | null
}

// Kişiye özel saat gövdesi.
export interface PersonalShiftDto {
  name?: string;
  workingHours: BranchWorkingHour[];
}

// Bir çalışanın tüm vardiya atamalarını (geçmiş dahil) getir.
export const getEmployeeShiftAssignments = async (
  employeeId: string
): Promise<AssignmentDto[]> => {
  const response = await axiosInstance.get<AssignmentDto[]>(
    `${API_ENDPOINTS.EMPLOYEES}/${employeeId}/shift-assignments`
  );
  return response.data;
};

// Belirli bir tarihte (varsayılan: bugün) geçerli atamayı getir (yoksa null).
export const getActiveAssignment = async (
  employeeId: string,
  date?: string
): Promise<AssignmentDto | null> => {
  const response = await axiosInstance.get<AssignmentDto | null>(
    `${API_ENDPOINTS.EMPLOYEES}/${employeeId}/shift-assignments/active`,
    { params: date ? { date } : undefined }
  );
  // Backend atama yoksa HTTP 204 (bos govde) doner -> null'a normalize et
  if (response.status === 204 || !response.data) return null;
  return response.data;
};

// Yeni vardiya ataması oluştur.
export const createAssignment = async (
  employeeId: string,
  dto: AssignmentCreateDto
): Promise<AssignmentDto> => {
  const response = await axiosInstance.post<AssignmentDto>(
    `${API_ENDPOINTS.EMPLOYEES}/${employeeId}/shift-assignments`,
    dto
  );
  return response.data;
};

// Bir vardiya atamasını sil.
export const deleteAssignment = async (
  employeeId: string,
  assignmentId: string
): Promise<void> => {
  await axiosInstance.delete(
    `${API_ENDPOINTS.EMPLOYEES}/${employeeId}/shift-assignments/${assignmentId}`
  );
};

// Çalışana kişiye özel saat ata (PERSONAL vardiya oluşturup atar).
export const setPersonalShift = async (
  employeeId: string,
  dto: PersonalShiftDto
): Promise<AssignmentDto> => {
  const response = await axiosInstance.post<AssignmentDto>(
    `${API_ENDPOINTS.EMPLOYEES}/${employeeId}/personal-shift`,
    dto
  );
  return response.data;
};

// Kullanıcı güncelleme (tüm alanlarla)
export const updateEmployee = async (id: string, data: UpdateEmployeeDto | AdminUpdateEmployeeDto): Promise<EmployeeDto> => {
  const response = await axiosInstance.put<EmployeeDto>(`${API_ENDPOINTS.EMPLOYEES}/${id}`, data);
  return response.data;
};

// Account-lifecycle actions — dedicated endpoints so a profile edit can never flip the live clock-in status.
// approve → ACTIVE (+ onay bildirimi), ban → BANNED, unban → INACTIVE.
export const approveEmployee = async (id: string): Promise<EmployeeDto> => {
  const response = await axiosInstance.post<EmployeeDto>(`${API_ENDPOINTS.EMPLOYEES}/${id}/approve`);
  return response.data;
};

export const banEmployee = async (id: string): Promise<EmployeeDto> => {
  const response = await axiosInstance.post<EmployeeDto>(`${API_ENDPOINTS.EMPLOYEES}/${id}/ban`);
  return response.data;
};

export const unbanEmployee = async (id: string): Promise<EmployeeDto> => {
  const response = await axiosInstance.post<EmployeeDto>(`${API_ENDPOINTS.EMPLOYEES}/${id}/unban`);
  return response.data;
};

// Kullanıcı silme
export const deleteUser = async (id: string): Promise<void> => {
  await axiosInstance.delete(`${API_ENDPOINTS.EMPLOYEES}/${id}`);
};

export const deleteEmployeeKey = async (id: string): Promise<void> => {
  await axiosInstance.delete(`${API_ENDPOINTS.EMPLOYEES}/deleteKey/${id}`);
};

// Çalışan cihaz kaydını sıfırla
export const resetDeviceRegistration = async (employeeId: string): Promise<void> => {
  await axiosInstance.post(`/api/mobile/reset-device/${employeeId}`);
};

// Kullanıcı oluşturma
export const createEmployee = async (data: Omit<UpdateEmployeeDto, 'id'>): Promise<EmployeeDto> => {
  const response = await axiosInstance.post<EmployeeDto>(API_ENDPOINTS.EMPLOYEES, data);
  return response.data;
};

// Email ile kullanıcı getirme
export const getEmployeeByEmail = async (email: string): Promise<EmployeeDto> => {
  const response = await axiosInstance.get<EmployeeDto>(`${API_ENDPOINTS.EMPLOYEES}/email/${email}`);
  return response.data;
};

// Şube çalışanlarını getir
export const getBranchEmployees = async (
  branchId: string,
  page = 0,
  size = 10,
  sortBy = 'firstName',
  sortDirection = 'asc'
): Promise<PageResponse<EmployeeDto>> => {
  try {
    const response = await axiosInstance.get(`${API_ENDPOINTS.EMPLOYEES}/by-branch/${branchId}`, {
      params: {
        page,
        size,
        sortBy,
        sortDirection
      }
    });
    
    // Backend'den gelen data'ya göre response format'ı
    if (response.data.content) {
      return response.data; // PageResponse format'ında geliyorsa
    }
    
    // Array olarak geliyorsa PageResponse format'ına çevir
    const employees = Array.isArray(response.data) ? response.data : [];
    return {
      content: employees,
      totalElements: employees.length,
      totalPages: Math.ceil(employees.length / size),
      size: employees.length,
      number: page
    };
  } catch (error) {
    if (isAxiosError(error)) {
      if (error.response?.status === 403) {
        throw new Error('Bu şubenin çalışanlarını görme yetkiniz yok');
      }
      if (error.response?.status === 404) {
        throw new Error('Şube bulunamadı');
      }
      throw new Error(error.response?.data?.message || 'Şube çalışanları getirilirken bir hata oluştu');
    }
    throw error;
  }
}; 
  